import classnames from 'classnames';
import { useField, useFormikContext } from 'formik';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connectAutoComplete } from 'react-instantsearch-dom';

// components
import Asterisk from '@atoms/Asterisk';
import CaretIcon from '@icons/CaretIcon';
import MagnifyingGlassIcon from '@icons/MagnifyingGlassIcon';
import LoadingInline from '@molecules/LoadingInline';
import { BodySmall, LabelLarge } from '@styled';
import {
    List,
    ListItem,
    ContainerStyled,
    LeftIconWrapper,
    RightIconWrapper,
    InputContainerStyled,
    CloseIconWrapperStyled,
    CaretIconWrapperStyled,
} from './index.styled';

// functionality
import colors from '@general/colors';
import useClickOutside from '@hooks/useClickOutside';

const AutocompleteCompanyNames = props => {
    const {
        currentRefinement,
        hits,
        id,
        isDisabled,
        initialRefinement = '',
        isLoading,
        label,
        listItemMinHeight,
        name,
        onClear,
        onSelect,
        required,
        refine,
        searchInputRef,
    } = props;

    const { values } = useFormikContext();
    const [field, meta, { setError, setTouched, setValue }] = useField(props);
    const [showList, setShowList] = useState(false);
    const [browsedHit, setBrowsedHit] = useState();
    const [hasChanged, setHasChanged] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        // Set the selectedCompany to the value on component load
        if (!meta.touched && meta.value) {
            setSelectedCompany(meta.value);
        }
    }, [meta.touched, meta.value]);

    useEffect(() => {
        setBrowsedHit(hits[0]);
    }, [hits]);

    useEffect(() => {
        if (values.independent) {
            handleIndependent();
        }
    }, [values.independent]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIndependent = async () => {
        await setValue('');
        refine('');
        setError('');
    };

    const handleSelect = async hit => {
        setShowList(false);

        const previouslySelectedCompany = selectedCompany;

        // Set newly selected company
        if (previouslySelectedCompany !== hit.name) {
            setSelectedCompany(hit.name);
        }

        refine(hit.name);
        await setValue(hit.name); // Current Workaround (https://github.com/jaredpalmer/formik/issues/2059).
        if (onSelect) {
            onSelect(hit);
        }
    };

    const onChange = e => {
        setHasChanged(true);
        setValue(e.target.value);
        refine(e.target.value);

        if (e.target.value !== '') {
            setShowList(true);
        }
    };

    const getClickOutsideException = target => {
        if (listRef.current && !listRef.current.contains(target)) return true;

        return false;
    };

    useClickOutside({
        ref: containerRef,
        onClickOutside: () => setShowList(false),
        exceptionCb: getClickOutsideException,
    });

    const onKeyDown = e => {
        // down
        if (e.keyCode === 40) {
            if (browsedHit && hits.length > browsedHit.__position) {
                setBrowsedHit(hits[browsedHit.__position]);
            }
        }
        // up
        if (e.keyCode === 38) {
            if (browsedHit && browsedHit.__position - 2 >= 0) {
                setBrowsedHit(hits[browsedHit.__position - 2]);
            }
        }
        // escape
        if (e.keyCode === 27) {
            setShowList(false);
            refine('');
            setValue('');
        }
        // return
        if (e.keyCode === 13) {
            e.preventDefault();
            if (browsedHit) {
                handleSelect(browsedHit);
            }
        }
        // tab
        if (e.keyCode === 9 && showList === true) {
            if (!meta.value || meta.value !== selectedCompany) {
                setValue(inputRef.current.value);
                setError('');
            }

            setShowList(false);
        }
    };

    const handleBlur = useCallback(
        e => {
            setTouched(true);
            // checking the relatedTarget value determines if the blur
            // event was due to clicking a company from the dropdown (
            // which would give a null value for relatedTarget) or moving
            // to a new field
            if (selectedCompany === '' && (e.relatedTarget !== null || !showList)) {
                setShowList(false);
                refine('');
                setValue('');
            }
        },
        [selectedCompany, showList, setTouched, refine, setValue]
    );

    const refinementValue = initialRefinement || currentRefinement;

    const textInputStyle = {
        padding: '1rem 3rem',
        width: '100%',
    };

    const hasError = meta.touched && meta.error;

    return (
        <div className={classnames({ 'has-danger': hasError })}>
            <LabelLarge className="form-control-label" htmlFor={id || name}>
                {label} {required && <Asterisk />}
            </LabelLarge>
            {isLoading && <LoadingInline />}
            {!isLoading && (
                <div>
                    <ContainerStyled ref={containerRef}>
                        <LeftIconWrapper
                            onClick={() => {
                                if (isDisabled) {
                                    return;
                                }

                                inputRef.current.focus();
                                setShowList(true);
                            }}
                        >
                            <MagnifyingGlassIcon fill={colors.darkGray} />
                        </LeftIconWrapper>

                        <InputContainerStyled>
                            <input
                                autoComplete="off"
                                className={classnames('form-control', { 'form-control-danger': hasError })}
                                {...field}
                                disabled={isDisabled}
                                ref={ref => {
                                    inputRef.current = ref;
                                    if (searchInputRef != null) {
                                        searchInputRef.current = ref;
                                    }
                                }}
                                label={label}
                                name={name}
                                onBlur={handleBlur}
                                onChange={e => {
                                    setTouched(true);
                                    onChange(e);
                                }}
                                onKeyDown={onKeyDown}
                                onClick={() => setShowList(!showList)}
                                placeholder="Check for Your Company by Name"
                                style={textInputStyle}
                                type="search"
                                value={!hasChanged ? values.companyName : refinementValue}
                            />

                            {hasError && <BodySmall className="mt-0.25rem text-error">{meta.error}</BodySmall>}
                            {showList && (
                                <List ref={listRef} hasLabel={!!label}>
                                    {hits.length === 0 && <ListItem>No matches found</ListItem>}
                                    {hits.map(hit => (
                                        <ListItem
                                            key={hit.objectID}
                                            onClick={() => handleSelect(hit)}
                                            className={browsedHit?.objectID === hit.objectID ? 'browsed-option' : ''}
                                            listItemMinHeight={listItemMinHeight}
                                            onMouseOver={() => setBrowsedHit(hit)}
                                        >
                                            <div>
                                                <p style={{ fontSize: '16px', marginBottom: 0, lineHeight: '1.4em' }}>
                                                    {hit.name}{' '}
                                                    {hit.location?.state ? (
                                                        <span style={{ color: colors.mediumGray, fontStyle: 'italic' }}>
                                                            ({hit.location.state})
                                                        </span>
                                                    ) : null}
                                                </p>
                                            </div>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </InputContainerStyled>

                        <RightIconWrapper className="ais-SearchBox-submit">
                            {(values.companyName || refinementValue) && (
                                <CloseIconWrapperStyled
                                    onClick={() => {
                                        if (isDisabled) {
                                            return;
                                        }

                                        inputRef.current.focus();
                                        setValue('');
                                        setShowList(false);
                                        refine('');
                                        setSelectedCompany('');
                                        onClear();
                                    }}
                                >
                                    <svg
                                        fill="#cccccc"
                                        height="20"
                                        width="20"
                                        viewBox="0 0 20 20"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
                                    </svg>
                                </CloseIconWrapperStyled>
                            )}

                            <CaretIconWrapperStyled
                                onClick={() => {
                                    if (isDisabled) {
                                        return;
                                    }

                                    inputRef.current.focus();
                                    setShowList(!showList);
                                }}
                            >
                                <CaretIcon fill={colors.darkGray} />
                            </CaretIconWrapperStyled>
                        </RightIconWrapper>
                    </ContainerStyled>
                </div>
            )}
        </div>
    );
};

AutocompleteCompanyNames.propTypes = {
    compHit: PropTypes.func,
    currentRefinement: PropTypes.any,
    hits: PropTypes.array,
    id: PropTypes.string,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    initialRefinement: PropTypes.string,
    label: PropTypes.string,
    listItemMinHeight: PropTypes.string,
    name: PropTypes.string,
    onClear: PropTypes.func.isRequired,
    onCreateNewCompany: PropTypes.func,
    onSelect: PropTypes.func,
    refine: PropTypes.any,
    required: PropTypes.bool,
    searchInputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default connectAutoComplete(AutocompleteCompanyNames);
